import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type { DraftPick } from "../../../common/types.ts";
import { groupByUnique } from "../../../common/utils.ts";
import { addPowerRankingsStuffToTeams } from "./powerRankings.ts";
import { getEstPicks } from "../core/team/valueChange.ts";

// M3-4: the standalone draft-picks page (view "draftPicks", routes
// /l/:lid/draft_picks[/:abbrev]) was retired into an in-place DraftPicksSheet
// MobileSheet opened from the Draft page, the Trade page and the market hub. The
// sheet fetches ONE team's pick assets (own holdings + the picks it owes out) ad
// hoc on open via the `getDraftPicksSheet` worker RPC (the M3 data pattern), not
// the page-view pipeline. processDraftPicks (the pick-value/projection shaping
// the old page carried) is kept here because it is the only place that owns it.
//
// getDraftPicksSheet returns a compact, sheet-digestible shape (one row per
// pick: season / round / original owner / pick or projected pick). Basketball
// GM draft picks carry no protection metadata, so there are no protection notes
// to surface. The return type is an anonymous structural type (no exported
// alias) so the RPC codegen (tools/genApiTypes.ts) expands it inline in
// common/types/.gen/workerApi.d.ts rather than emitting an unresolved name.

const adjustProjectedPick = ({
	projectedPick,
	numSeasons,
	numTeams,
}: {
	projectedPick: number;
	numSeasons: number;
	numTeams: number;
}) => {
	if (numSeasons <= 0) {
		return projectedPick;
	}

	const averagePick = numTeams / 2;

	const NUM_SEASONS_CUTOFF = 8;

	// Many years in the future, we know basically nothing
	if (numSeasons >= NUM_SEASONS_CUTOFF) {
		return Math.round(averagePick);
	}

	// Before then, average projectedPick and averagePick with increasing weight
	const fractionProjectedPick = 1 - numSeasons / NUM_SEASONS_CUTOFF;
	return Math.round(
		fractionProjectedPick * projectedPick +
			(1 - fractionProjectedPick) * averagePick,
	);
};

export const processDraftPicks = async (draftPicksRaw: DraftPick[]) => {
	// Annotate the accumulator so the pick-value shaping keeps concrete field
	// types (an empty-array push otherwise degrades to any[], which would leak
	// into the getDraftPicksSheet RPC codegen as Array<{ …: any }>).
	const draftPicks: (DraftPick & {
		abbrev: string;
		originalAbbrev: string;
		powerRanking: number;
		projectedPick: number | undefined;
	})[] = [];

	const teamsRaw = await idb.getCopies.teamsPlus(
		{
			attrs: ["tid", "abbrev", "playThroughInjuries"],
			seasonAttrs: ["lastTen", "won", "lost", "tied", "otl"],
			stats: ["gp", "mov"],
			season: g.get("season"),
			showNoStats: true,
		},
		"noCopyCache",
	);

	const teamsWithRankings = await addPowerRankingsStuffToTeams(
		teamsRaw,
		g.get("season"),
		"regularSeason",
	);

	const teams = groupByUnique(teamsWithRankings, "tid");

	let estPicksCache;

	for (const dp of draftPicksRaw) {
		const t = teams[dp.originalTid];

		let projectedPick;
		if (dp.pick === 0 && typeof dp.season === "number") {
			if (!estPicksCache) {
				const teamOvrsSorted = teamsWithRankings
					.map((t) => {
						return {
							ovr: t.powerRankings.ovr,
							tid: t.tid,
						};
					})
					.sort((a, b) => b.ovr - a.ovr);
				const { estPicks } = await getEstPicks(teamOvrsSorted);
				estPicksCache = estPicks;
			}

			projectedPick = adjustProjectedPick({
				projectedPick: estPicksCache[dp.originalTid]!,
				numSeasons: dp.season - g.get("season"),
				numTeams: teamsWithRankings.length,
			});
		}

		draftPicks.push({
			...dp,
			abbrev: teams[dp.tid]?.abbrev ?? "???",
			originalAbbrev: t?.abbrev ?? "???",
			powerRanking: t?.powerRankings.rank ?? Infinity,
			projectedPick,
		});
	}

	return draftPicks;
};

export const getDraftPicksSheet = async (tid: number) => {
	const draftPicksRaw = (await idb.cache.draftPicks.getAll()).filter(
		(dp) => dp.tid === tid || dp.originalTid === tid,
	);

	const draftPicksProcessed = await processDraftPicks(draftPicksRaw);

	const toRow = (dp: (typeof draftPicksProcessed)[number]) => ({
		dpid: dp.dpid,
		season: dp.season,
		round: dp.round,
		originalTid: dp.originalTid,
		originalAbbrev: dp.originalAbbrev,
		pick: dp.pick,
		projectedPick: dp.projectedPick,
	});

	// Split into what this team currently holds (own-original + acquired) vs the
	// picks that originally belonged to it but are now owed to another team.
	// filter().map() (not push into an empty array) so the RPC codegen infers a
	// concrete row type instead of any[].
	return {
		tid,
		abbrev: helpers.getAbbrev(tid),
		draftPicks: draftPicksProcessed.filter((dp) => dp.tid === tid).map(toRow),
		draftPicksOutgoing: draftPicksProcessed
			.filter((dp) => dp.originalTid === tid && dp.tid !== tid)
			.map(toRow),
	};
};
